// src/components/common/QrScannerModal.jsx
// Scanner de QR code plein écran (caméra) utilisé pour confirmer une livraison :
// le client affiche son code, le livreur le scanne ici — preuve mutuelle de la
// remise du colis (protège le livreur comme le client en cas de litige).
import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { motion } from 'framer-motion';
import { X, QrCode, AlertTriangle } from 'lucide-react';

const SCANNER_ELEMENT_ID = 'delivery-qr-scanner';

const QrScannerModal = ({ onScan, onClose }) => {
  const scannerRef = useRef(null);
  // html5-qrcode lève une exception SYNCHRONE ("Cannot stop, scanner is not
  // running or paused") si .stop() est appelé alors qu'il ne tourne pas déjà
  // — un simple .catch() ne suffit pas à l'intercepter. On suit nous-mêmes
  // l'état réel pour ne jamais appeler .stop() plus d'une fois (ex: une fois
  // après un scan réussi, puis une seconde fois au démontage du composant).
  const runningRef = useRef(false);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(true);

  const safeStop = async () => {
    if (!scannerRef.current || !runningRef.current) return;
    runningRef.current = false;
    try {
      await scannerRef.current.stop();
    } catch (_) {
      // Déjà arrêté / jamais démarré — rien à faire.
    }
  };

  useEffect(() => {
    let cancelled = false;
    const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID);
    scannerRef.current = scanner;

    scanner
      .start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decodedText) => {
          if (cancelled) return;
          cancelled = true;
          safeStop().finally(() => onScan(decodedText));
        },
        () => {
          // Erreurs de décodage frame-par-frame — normal tant qu'aucun code n'est visible, on ignore.
        }
      )
      .then(() => {
        if (cancelled) {
          // Le composant a été démonté pendant le démarrage de la caméra —
          // le stream est bien actif maintenant, il faut l'arrêter.
          runningRef.current = true;
          safeStop();
          return;
        }
        runningRef.current = true;
        setStarting(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setStarting(false);
        setError(
          err?.name === 'NotAllowedError'
            ? 'Autorisez l\'accès à la caméra pour scanner le QR code du client.'
            : 'Impossible d\'accéder à la caméra.'
        );
      });

    return () => {
      cancelled = true;
      safeStop().finally(() => {
        scannerRef.current?.clear?.();
      });
    };
  }, [onScan]);

  const handleClose = () => {
    safeStop().finally(onClose);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black z-[60] flex flex-col"
    >
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-2 text-white">
          <QrCode className="w-5 h-5" />
          <span className="font-semibold">Scanner le code du client</span>
        </div>
        <button
          onClick={handleClose}
          className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center text-white"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center px-6">
        {error ? (
          <div className="text-center text-white">
            <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <p className="text-sm text-gray-200">{error}</p>
          </div>
        ) : (
          <div className="w-full max-w-sm">
            <div id={SCANNER_ELEMENT_ID} className="rounded-2xl overflow-hidden" />
            {starting && (
              <p className="text-center text-gray-300 text-sm mt-4">Démarrage de la caméra...</p>
            )}
          </div>
        )}
      </div>

      <div className="p-6 text-center">
        <p className="text-gray-300 text-sm">
          Demandez au client d'ouvrir son suivi de commande et de vous montrer le QR code affiché à l'écran.
        </p>
      </div>
    </motion.div>
  );
};

export default QrScannerModal;
